import React, {useEffect, useMemo, useState, useCallback} from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  UncontrolledDropdown,
  Button,
} from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import DeleteModal from "../../Components/Common/DeleteModal";
import { useDispatch, useSelector } from "react-redux";
import { getUser, addUser, updateUser, deleteUser} from "../../slices/users/thunk";
import {getRol} from "../../slices/roles/thunks";
import { getEstablishments, getCompanies } from "../../slices/companies/thunk";
import UserModal, { UserModalMode } from "./Components/UserModal";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const UserList = () => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalMode, setUserModalMode] = useState<UserModalMode>("view");
  const [isSavingUser, setIsSavingUser] = useState(false);
  const dispatch = useDispatch<any>();
  const establishments = useSelector((state: any) => state.Companies.establishmentList || []);
  const companyList = useSelector((state: any) => state.Companies.companyList || []);
  const rol = useSelector((state: any) => state.Roles?.rolLists || []);
  const listUser = useSelector((state: any) => state.Users.userLists);
  const [companyId, setCompanyId] = useState<string | null>(null);

  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
        try {
            const parsedUser = JSON.parse(authUser);
            if (parsedUser && parsedUser.companyId) {
                setCompanyId(parsedUser.companyId);
            }
        } catch (error) {
            console.error("Error parsing authUser from localStorage", error);
        }
    }
  }, []);

  useEffect(() => {
    let query = '';
    if (companyId) {
        query = `companyId=${companyId}`;
    }
    dispatch(getUser(query));
    dispatch(getRol(undefined));
    dispatch(getEstablishments());
    dispatch(getCompanies());
  }, [dispatch, companyId]);

  const onClickDelete = useCallback((user: any) => {
    setSelectedUser(user);
    setDeleteModal(true);
  }, []);

  const handleDeleteUser = async () => {
    if (selectedUser) {
        await dispatch(deleteUser(selectedUser.id || selectedUser._id));
        let query = '';
        if (companyId) {
            query = `companyId=${companyId}`;
        }
        dispatch(getUser(query));
    }
    setSelectedUser(null);
    setDeleteModal(false);
  };

  const openViewUser = useCallback((user: any) => {
    setSelectedUser(user);
    setUserModalMode("view");
    setUserModalOpen(true);
  }, []);

  const openEditUser = useCallback((user: any) => {
    setSelectedUser(user);
    setUserModalMode("edit");
    setUserModalOpen(true);
  }, []);

  const openAddUser = () => {
    setSelectedUser(null);
    setUserModalMode("add");
    setUserModalOpen(true);
  };

  const closeUserModal = () => {
    setUserModalOpen(false);
    setSelectedUser(null);
    setIsSavingUser(false);
  };

  const handleSubmitUser = async (payload: any) => {
    setIsSavingUser(true);

  const action = userModalMode === "add" ? addUser(payload) : updateUser(payload);
    
    try {
      const resultAction = await dispatch(action);

      if (addUser.fulfilled.match(resultAction) || updateUser.fulfilled.match(resultAction)) {
        let query = '';
        if (companyId) {
            query = `companyId=${companyId}`;
        }
        dispatch(getUser(query));
        closeUserModal();
      }
    } catch (error) {
      console.error("Failed to dispatch user action:", error);
    } finally {
      setIsSavingUser(false);
    }
  };

  const columns = useMemo(
      () => [
      {
          header: "Nombre",
          accessorKey: "name",
      },
      {
          header: "Documento",
          accessorKey: "document",
      },
      {
          header: "Correo electrónico",
          accessorKey: "email",
      },
      {
          header: "Establecimiento",
          accessorFn: (row: any) => row?.establishment?.code || row?.establishment?.id || "—",
      },
      {
          header: "Punto de emisión",
          accessorFn: (row: any) => row?.emissionPoint?.code || row?.emissionPoint?.id || "—",
      },
      {
          header: "Rol",
          accessorFn: (row: any) => row?.roles[0]?.name || row?.roles[0]?.id || "—",
      },

        {
          header: "Acciones",
          cell: (cellProps: any) => (
            <UncontrolledDropdown className="text-center">
                <div className="d-flex gap-2">
                    <Button
                        color="primary"
                        size="sm"
                        title="Ver"
                        onClick={() => openViewUser(cellProps.row.original)}
                    >
                        <i className="ri-eye-line"></i>
                    </Button>
                    <Button
                        color="warning"
                        size="sm"
                        title="Editar"
                        onClick={() => openEditUser(cellProps.row.original)}
                    >
                        <i className="ri-pencil-line"></i>
                    </Button>
                    <Button
                        color="danger"
                        size="sm"
                        title="Eliminar"
                        onClick={() => onClickDelete(cellProps.row.original)}
                    >
                        <i className="ri-delete-bin-line"></i>
                    </Button>

                </div>

            </UncontrolledDropdown>
          ),
        },
      ],
      [onClickDelete, openViewUser, openEditUser]
  );

  document.title = "Usuarios | Vuelo";

  return (
      <React.Fragment>
        <DeleteModal
            show={deleteModal}
            onDeleteClick={handleDeleteUser}
            onCloseClick={() => setDeleteModal(false)}
        />

        <div className="page-content">
          <Container fluid>
              <BreadCrumb title="Usuarios" pageTitle="Inicio" />
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Usuarios del sistema</h5>
                <Button color="primary" onClick={openAddUser}>
                  <i className="ri-add-line align-bottom me-1"></i> Agregar Usuario
                </Button>
              </CardHeader>
              <CardBody>
                <UserModal
                  isOpen={userModalOpen}
                  mode={userModalMode}
                  user={selectedUser}
                  roles={rol}
                  establishment={establishments}
                  companies={companyList}
                  isSaving={isSavingUser}
                  onClose={closeUserModal}
                  onSubmit={handleSubmitUser}
                />
                <TableContainer
                    columns={columns}
                    data={listUser || []}
                    isGlobalFilter={true}
                    customPageSize={10}
                    theadClass="table-light text-muted"
                    SearchPlaceholder="Buscar por nombre, correo o rol..."
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                />
              </CardBody>
            </Card>
          </Container>
        </div>
      </React.Fragment>
  );
};

export default UserList;
